/* eslint-disable react/no-unused-prop-types */
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import _get from 'lodash/get';
import TruncateText from '../TruncateText/TruncateText';
import {
  getToolIds,
  isLocalToolLaunch,
  isTabToolLaunch,
  formatName,
} from '@macmillan-learning/gradebook-shared';
import { getGradebook } from '../../actions/gradebook';
import { setScrollToItem, setHighlightToItems } from '../../actions/contentUi';
import { getAssignmentCompletionStatuses } from '../../actions/assignmentCompletionStatuses';
import { getConfigObject } from '../../utils/config';

const truncateContent = (text) => (
  <TruncateText
    button={false}
    tooltipPlacement="right"
    text={text}
    textVariant="body2"
  />
);

const isAssignmentEnabled = (assignment) => {
  const { tool } = assignment;
  const disabledTools = [getToolIds().pathfinder];
  const toolIsEnable = !disabledTools.includes(tool);
  return toolIsEnable;
};

const MLToolLaunchLink = (props) => {
  const {
    data,
    testId,
    contentUiScope,
    launchAction,
    linkLabel,
    children,
    isStudent,
    dispatch,
    className,
    courseId,
    truncate,
    isLinkDisabled,
    getLaunchUrl,
    handleLocalLaunch,
  } = props;

  const launchUrl = getLaunchUrl({
    ...data,
    actual_course_id: courseId,
  });
  const { FF_FORCE_IHUB_LAUNCHES } = getConfigObject();
  const isLocalLink = FF_FORCE_IHUB_LAUNCHES || isLocalToolLaunch(data.tool);
  const isNewTab = isTabToolLaunch(data.tool);
  const gradebookReflowForNewTab = () =>
    isStudent ? dispatch(getGradebook(data.course_id)) : null;

  let newActivityName;
  if (linkLabel) {
    newActivityName = linkLabel;
  } else {
    const activityName = formatName(data.name, data.tool, data.createdAt);
    newActivityName = activityName;
  }

  const formattedActivityName = truncate
    ? truncateContent(newActivityName)
    : newActivityName;
  if (isLinkDisabled) {
    return formattedActivityName;
  }

  const onClickHandler = (event) => {
    dispatch(setHighlightToItems([data.id], contentUiScope));
    dispatch(setScrollToItem(true, contentUiScope));
    gradebookReflowForNewTab();
    if (!isLocalLink && isStudent) {
      dispatch(getAssignmentCompletionStatuses(data.course_id));
    } else if (isLocalLink) {
      // Local links use CW's reactor router, need to stop them from <a> behavior
      event.preventDefault();
      const openInNewTab =
        isNewTab || event.ctrlKey || event.shiftKey || event.metaKey;
      handleLocalLaunch(launchUrl, openInNewTab);
    }
  };

  if (launchAction) {
    return (
      <a
        href={launchUrl}
        className={className}
        onClick={launchAction(data.activity_id, data.id)}
        data-test-id={testId}
      >
        {formattedActivityName}
      </a>
    );
  }

  const linkContent = children || formattedActivityName;

  const externalLink = (
    <a
      href={launchUrl}
      className={className}
      target={isNewTab ? '_blank' : '_self'}
      onClick={onClickHandler}
      data-test-id={testId}
      aria-label={
        isNewTab ? `${data.name} Opens in a new windows` : `${data.name}`
      }
      rel="noreferrer"
    >
      {linkContent}
    </a>
  );

  const localLink = isAssignmentEnabled(data) ? (
    <a
      onClick={onClickHandler}
      className={className}
      data-test-id={testId}
      aria-label={data.name}
      href={launchUrl}
    >
      {linkContent}
    </a>
  ) : (
    <p>{formattedActivityName}</p>
  );

  return isLocalLink ? localLink : externalLink;
};

MLToolLaunchLink.propTypes = {
  data: PropTypes.object,
  testId: PropTypes.string,
  contentUiScope: PropTypes.string,
  isTOC: PropTypes.bool,
  launchAction: PropTypes.func,
  linkLabel: PropTypes.string,
  children: PropTypes.node,
  isStudent: PropTypes.bool,
  restrictedAccess: PropTypes.bool,
  courseType: PropTypes.string,
  dispatch: PropTypes.func,
  className: PropTypes.string,
  courseId: PropTypes.string,
  cellWidth: PropTypes.number,
  truncate: PropTypes.bool,
  isLinkDisabled: PropTypes.bool,
  getLaunchUrl: PropTypes.func,
  handleLocalLaunch: PropTypes.func,
  isSection: PropTypes.bool,
  isSectionManager: PropTypes.bool,
};

function mapStateToProps(state) {
  const courseId = _get(state, 'gradebook.courseId');
  return {
    courseId,
  };
}

export default connect(mapStateToProps)(MLToolLaunchLink);
